/**
 * Component added in ticket https://coursera.atlassian.net/browse/DEGCON-301
 * Extracted at a glance block from static/bundles/degree-description/components/modules/hero/EoiAtAGlanceBlock.tsx
 * In order to make DDP 2.3 update more isolated
 */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { memo } from 'react';
import type { ReactElement } from 'react';

import { useParams } from 'js/lib/useRouter';

import { Typography2, typography2 } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import type { DdpFieldsAboveFoldFragment } from 'bundles/degree-description/__generated__/contentfulTypes';
import { HideOnMobile } from 'bundles/degree-description/components/shared/BreakpointDisplay';
import Container from 'bundles/degree-description/components/shared/Container';
import { useDegreeDescriptionPageData } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import { getManualCopyTest } from 'bundles/degree-description/utils/manualCopyTests';

type AtAGlanceHorizontalBlockProps = {
  atAGlance?: DdpFieldsAboveFoldFragment['atAGlance'];
};

type AtAGlanceTitles =
  | 'bannerAtAGlanceTitle1'
  | 'bannerAtAGlanceTitle2'
  | 'bannerAtAGlanceTitle3'
  | 'bannerAtAGlanceTitle4'
  | 'bannerAtAGlanceTitle5';
type AtAGlanceDescriptions =
  | 'bannerAtAGlanceDescription1'
  | 'bannerAtAGlanceDescription2'
  | 'bannerAtAGlanceDescription3'
  | 'bannerAtAGlanceDescription4'
  | 'bannerAtAGlanceDescription5';

const styles = {
  atAGlanceWrapper: css`
    position: absolute;
    left: 0;
    right: 0;
  `,
  atAGlanceHorizontal: css`
    display: flex;
    box-shadow: 0 8px 16px rgb(0 0 0 / 16%);
    border-radius: 8px;
    padding: var(--cds-spacing-200) 0;
    margin-top: var(--cds-spacing-300);
    background: var(--cds-color-neutral-background-primary);

    /* translateY(-48px) is for banner image padding-bottom */
    transform: translateY(-50%) translateY(calc(var(--cds-spacing-600) * -1));

    > :last-of-type {
      border-right: none;
    }

    .rc-Markdown.styled {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      p {
        font-family: var(--cds-font-family-source-sans-pro);
      }

      a {
        &:hover,
        &:focus {
          text-decoration: none;
        }

        &:visited {
          color: var(--cds-color-interactive-primary-selected);
        }
      }

      h2 {
        color: var(--cds-color-neutral-primary);
        ${typography2.subtitleLarge}
        margin: 0;
      }

      p {
        color: var(--cds-color-neutral-primary-weak);
        ${typography2.bodySecondary}
        margin: 0;
      }
    }
  `,
  atAGlanceHorizontalItem: css`
    flex: 1 1 0;
    border-right: 2px solid var(--cds-color-neutral-stroke-primary-weak);
    padding: 0 var(--cds-spacing-300) var(--cds-spacing-100);
  `,
  atAGlanceTitle: css`
    margin: 0 0 var(--cds-spacing-50);
  `,
};

const AtAGlanceHorizontalBlock = ({ atAGlance }: AtAGlanceHorizontalBlockProps): ReactElement | null => {
  let atAGlanceArray: Array<{ title?: string | null; description?: string | null }> = [];
  const { id } = useDegreeDescriptionPageData();
  const degreeId = id.replace(/^base~/, '');
  const { slug } = useParams();

  // DDP Manual Copy Tests
  const manualCopyTestData = slug ? getManualCopyTest(slug) : undefined;

  if (!atAGlance) return null;

  if (!manualCopyTestData || manualCopyTestData?.manualCopyTestType !== 'atAGlance') {
    for (let i = 1; i <= 5; i += 1) {
      let currTitle = atAGlance[`bannerAtAGlanceTitle${i}` as AtAGlanceTitles];
      let currDescription = atAGlance[`bannerAtAGlanceDescription${i}` as AtAGlanceDescriptions];

      if (manualCopyTestData?.manualCopyTestType === 'tuition' && manualCopyTestData.position === i) {
        currTitle = manualCopyTestData.title;
        currDescription = manualCopyTestData.description;
      }

      atAGlanceArray.push({ title: currTitle, description: currDescription });
    }
  } else if (manualCopyTestData?.manualCopyTestType === 'atAGlance') {
    atAGlanceArray = manualCopyTestData.atAGlance;
  }

  return (
    <HideOnMobile>
      <Container css={styles.atAGlanceWrapper}>
        <div className="at-a-glance" css={styles.atAGlanceHorizontal}>
          {atAGlanceArray.map(
            (item) =>
              (item.title || item.description) && (
                <div css={styles.atAGlanceHorizontalItem} key={item.title}>
                  {item.title && (
                    <Typography2 css={styles.atAGlanceTitle} component="h2" variant="subtitleLarge">
                      {item.title}
                    </Typography2>
                  )}

                  {item.description && (
                    <Markdown
                      source={item.description}
                      buttonTrackingNameV3="degree_info_link"
                      trackingSection="hero_banner"
                      trackingProductId={degreeId}
                    />
                  )}
                </div>
              )
          )}
        </div>
      </Container>
    </HideOnMobile>
  );
};

export default memo(AtAGlanceHorizontalBlock);
